/*  Private Device Vault — IndexedDB wrapper.
    iter237: namespaced to fully isolate Health and Item compartments. Each
    namespace maps to its OWN IndexedDB database — meta/vault/vault_blobs
    stores are duplicated per namespace so a key, cache or thumbnail from one
    compartment can NEVER leak into the other. Namespaces also get their own
    salt in meta, which means their derived AES keys are cryptographically
    independent even when the traveller uses the same PIN for both.
    Backwards compatible: legacy callers (no `ns` arg) read/write the original
    `TravelerPulsePrivateVault` database so existing vaults keep working. */

const DB_BASE = "TravelerPulsePrivateVault";
const DB_VERSION = 1;
const STORE_META  = "meta";
const STORE_VAULT = "vault";
const STORE_BLOBS = "vault_blobs";

/** Supported namespaces. "default" is the legacy, pre-iter237 shared DB. */
export const VAULT_NS = Object.freeze({ DEFAULT: "default", HEALTH: "health", ITEMS: "items" });

function _dbName(ns) {
  if (!ns || ns === VAULT_NS.DEFAULT) return DB_BASE;
  return `${DB_BASE}_${ns}`;
}

function _open(ns) {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(_dbName(ns), DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_META))  db.createObjectStore(STORE_META);
      if (!db.objectStoreNames.contains(STORE_VAULT)) db.createObjectStore(STORE_VAULT);
      if (!db.objectStoreNames.contains(STORE_BLOBS)) db.createObjectStore(STORE_BLOBS);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

function _tx(db, store, mode) { return db.transaction(store, mode).objectStore(store); }
function _wrap(req) { return new Promise((res, rej) => { req.onsuccess = () => res(req.result); req.onerror = () => rej(req.error); }); }

export async function metaGet(key, ns) {
  const db = await _open(ns);
  try { return await _wrap(_tx(db, STORE_META, "readonly").get(key)); } finally { db.close(); }
}
export async function metaSet(key, value, ns) {
  const db = await _open(ns);
  try { await _wrap(_tx(db, STORE_META, "readwrite").put(value, key)); } finally { db.close(); }
}
export async function metaDelete(key, ns) {
  const db = await _open(ns);
  try { await _wrap(_tx(db, STORE_META, "readwrite").delete(key)); } finally { db.close(); }
}
export async function vaultGet(compartmentId, ns) {
  const db = await _open(ns);
  try { return await _wrap(_tx(db, STORE_VAULT, "readonly").get(compartmentId)); } finally { db.close(); }
}
export async function vaultPut(compartmentId, encryptedPayload, ns) {
  const db = await _open(ns);
  try { await _wrap(_tx(db, STORE_VAULT, "readwrite").put(encryptedPayload, compartmentId)); } finally { db.close(); }
}
export async function vaultListKeys(ns) {
  const db = await _open(ns);
  try { return await _wrap(_tx(db, STORE_VAULT, "readonly").getAllKeys()); } finally { db.close(); }
}
export async function vaultDump(ns) {
  const db = await _open(ns);
  try {
    const store = _tx(db, STORE_VAULT, "readonly");
    const keys = await _wrap(store.getAllKeys());
    const values = await _wrap(store.getAll());
    const out = {};
    keys.forEach((k, i) => (out[k] = values[i]));
    return out;
  } finally { db.close(); }
}
export async function blobPut(id, blob, ns) {
  const db = await _open(ns);
  try { await _wrap(_tx(db, STORE_BLOBS, "readwrite").put(blob, id)); } finally { db.close(); }
}
export async function blobGet(id, ns) {
  const db = await _open(ns);
  try { return await _wrap(_tx(db, STORE_BLOBS, "readonly").get(id)); } finally { db.close(); }
}
export async function blobDelete(id, ns) {
  const db = await _open(ns);
  try { await _wrap(_tx(db, STORE_BLOBS, "readwrite").delete(id)); } finally { db.close(); }
}

/** Wipe one namespace (or all if no `ns`). */
export async function wipeEverything(ns) {
  const names = ns ? [_dbName(ns)] : [DB_BASE, `${DB_BASE}_health`, `${DB_BASE}_items`];
  await Promise.all(names.map((n) => new Promise((res) => {
    const req = indexedDB.deleteDatabase(n);
    req.onsuccess = () => res();
    req.onerror   = () => res();
    req.onblocked = () => res();
  })));
}

export async function requestPersistentStorage() {
  if (!navigator.storage || !navigator.storage.persist) return { supported: false, granted: false };
  const already = await navigator.storage.persisted();
  if (already) return { supported: true, granted: true };
  const granted = await navigator.storage.persist();
  return { supported: true, granted };
}

export async function storageEstimate() {
  if (!navigator.storage || !navigator.storage.estimate) return null;
  return navigator.storage.estimate();
}
